// User Management Page with Modal for Add/Edit
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Space, Button, Tag, Modal, notification, Switch, Select, Spin } from 'antd';
import { formatDate, toTitleCase } from '../../utils/helpers';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
	ThemeButton,
	ThemeFilterBtn,
	ThemeForm,
	ThemeInput,
	ThemeMainWrapper,
	ThemeTable,
	ThemeTableCard,
	ThemeModal,
} from '../Styles/Theme';
import { ForbiddenWrapper } from './UserManagementStyle';
import MobHeader from '../../Components/Header/MobHeader';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  getUsersApi,
  getUsersReset,
  deleteUserApi,
  deleteUserReset,
  createUserApi,
  createUserReset,
  updateUserApi,
  updateUserReset,
  getUserByIdApi,
  getUserByIdReset,
  resendInvitationApi,
  resendInvitationReset,
} from '../../store/actions/userAction';
import { getRolesDropdownApi } from '../../store/actions/roleAction';
import { getOrganizationsDropdownApi } from '../../store/actions/organizationAction';
import { useUserPermissions } from '../../utils/permissions';
import { USER_TYPES, USER_TYPE_OPTIONS } from '../../utils/constants';
import StatusTag from '../../Components/StatusTag';
import { handleInputChange, handleSelectChange, filterByLabel } from '../../utils/formFunctions';

const UserManagement = (props) => {
	const {
		getUsers,
		getUsersReset,
		getUsersState,
		deleteUser,
		deleteUserReset,
		deleteUserState,
		createUser,
		createUserReset,
		createUserState,
		updateUser,
		updateUserReset,
		updateUserState,
		getUserById,
		getUserByIdReset,
		getUserByIdState,
		getRolesDropdown,
		getRolesDropdownState,
		getOrganizationsDropdown,
		getOrganizationsDropdownState,
		resendInvitation,
		resendInvitationReset,
		resendInvitationState,
	} = props;

  const [form] = ThemeForm.useForm();
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const defaultFilters = {
    page: 1,
    limit: 12,
    search: '',
    role: '',
    organization: '',
    sort: 'createdAt',
    sortDirection: 'descend',
  };
  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    for (const [key, value] of urlParams.entries()) {
      urlFilters[key] = value;
    }
    const filters = {
      ...defaultFilters,
      ...urlFilters,
    };
    return filters;
  };
  const initialFilters = getFiltersFromUrl();
  const [filters, setFilters] = useState(initialFilters);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState([false, '', null]); // [isOpen, title, editId]
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    user_type: null,
    role: null,
    organization: null,
    is_active: true,
  });
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);
  // Check permissions using hook
  const { can, permissions, isSuperAdmin, userData, userType } =
    useUserPermissions();
  const currentUserOrganizationId = userData?.organization_id;
  const canCreate = can('create');
  const canEdit = can('edit');
  const canDelete = can('delete');

  const isEdit = modalOpen[2] !== null;

	// Fetch roles and organizations on mount
	useEffect(() => {
		if (permissions.authorized) {
			getRolesDropdown();
			getOrganizationsDropdown();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [permissions.authorized]);

	// Handle URL-based pagination and filters
	useEffect(() => {
		const updatedFilters = getFiltersFromUrl();
		setFilters({ ...updatedFilters });
		if (permissions.authorized) {
			getUsers({ ...updatedFilters });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.location.search]);

	// Handle users response
	useEffect(() => {
		if (getUsersState.apiState === 'success' && getUsersState.data) {
			// Handle both response structures: { users: [...] } or { data: { users: [...] } } or { all_users: [...] }
			const usersData = getUsersState.data.data || getUsersState.data;
			const users = usersData.users || usersData.all_users || [];
			if (users && users.length > 0) {
				setTotalRecords(usersData.pagination?.total || usersData.total_count || getUsersState.data.pagination?.total || getUsersState.data.total_count || 0);
				const updatedUsers = users.map((item) => ({
					key: item._id,
					id: item._id,
					name: item.name,
					email: item.email,
					mobile: item.mobile || '-',
					user_type: item.user_type || '-',
					role: item.role?.display_name || item.role?.role || '-',
					organization: item.organization?.name || '-',
					is_active: item.is_active !== false, // Default to true if not specified
					createdAt: formatDate(item.createdAt),
				}));
				setTableData(updatedUsers);
			} else {
				setTableData([]);
				setTotalRecords(0);
			}
			getUsersReset();
		} else if (getUsersState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: getUsersState.message || 'Failed to fetch users',
			});
			getUsersReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getUsersState]);

	// Handle create response
	useEffect(() => {
		if (createUserState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: createUserState.message || 'User created successfully',
			});
			handleCloseModal();
			const updatedFilters = getFiltersFromUrl();
			getUsers({ ...updatedFilters });
		} else if (createUserState.apiState === 'error') {
			// Handle validation errors
			if (createUserState.errors) {
				for (const fieldName in createUserState.errors) {
					if (createUserState.errors.hasOwnProperty(fieldName)) {
						const errorMessage = createUserState.errors[fieldName];
						form.setFields([
							{
								name: fieldName,
								errors: [
									errorMessage.replace(
										fieldName,
										toTitleCase(fieldName),
									),
								],
							},
						]);
					}
				}
			}
			notification.error({
				message: 'Error',
				description: createUserState.message || 'Failed to create user',
			});
		}
		createUserReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createUserState]);

	// Handle update response
	useEffect(() => {
		if (updateUserState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: updateUserState.message || 'User updated successfully',
			});
			handleCloseModal();
			const updatedFilters = getFiltersFromUrl();
			getUsers({ ...updatedFilters });
		} else if (updateUserState.apiState === 'error') {
			// Handle validation errors
			if (updateUserState.errors) {
				for (const fieldName in updateUserState.errors) {
					if (updateUserState.errors.hasOwnProperty(fieldName)) {
						const errorMessage = updateUserState.errors[fieldName];
						form.setFields([
							{
								name: fieldName,
								errors: [
									errorMessage.replace(
										fieldName,
										toTitleCase(fieldName),
									),
								],
							},
						]);
					}
				}
			}
			notification.error({
				message: 'Error',
				description: updateUserState.message || 'Failed to update user',
			});
		}
		updateUserReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateUserState]);

	// Handle delete response
	useEffect(() => {
		if (deleteUserState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: deleteUserState.message || 'User deleted successfully',
			});
			setDeleteModalVisible(false);
			setSelectedUser(null);
			const updatedFilters = getFiltersFromUrl();
			getUsers({ ...updatedFilters });
		} else if (deleteUserState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: deleteUserState.message || 'Failed to delete user',
			});
		}
		deleteUserReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteUserState]);

	// Handle resend invitation response
	useEffect(() => {
		if (resendInvitationState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: resendInvitationState.message || 'Invitation resent successfully',
			});
		} else if (resendInvitationState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: resendInvitationState.message || 'Failed to resend invitation',
			});
		}
		resendInvitationReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resendInvitationState]);

	// Fetch user data when editing
	useEffect(() => {
		if (modalOpen[0] && modalOpen[2]) {
			setIsModalLoading(true);
			getUserById({ id: modalOpen[2] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOpen]);

	// Handle user data response for edit
	useEffect(() => {
		if (getUserByIdState.apiState === 'loading') {
			setIsModalLoading(true);
		} else if (getUserByIdState.apiState === 'success' && getUserByIdState.data && modalOpen[2]) {
			const user = getUserByIdState.data;
			const userData = {
				name: user.name || '',
				email: user.email || '',
				mobile: user.mobile || '',
				user_type: user.user_type || null,
				role: user.role?._id || null,
				organization: user.organization?._id || null,
				is_active: user.is_active !== false, // Default to true
			};
			setFormData(userData);
			form.setFieldsValue(userData);
			setIsModalLoading(false);
		} else if (getUserByIdState.apiState === 'error') {
			setIsModalLoading(false);
			notification.error({
				message: 'Error',
				description: getUserByIdState.message || 'Failed to fetch user',
			});
			handleCloseModal();
		}
		if (getUserByIdState.apiState !== 'loading') {
			getUserByIdReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getUserByIdState, modalOpen]);

	const handleSearch = (value) => {
		const queryParams = new URLSearchParams(window.location.search);
		queryParams.set('search', value);
		queryParams.set('page', '1');
		const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
		window.history.pushState({}, '', newUrl);
		setFilters({ ...filters, search: value, page: 1 });
	};

	const handleTableChange = (pagination, _, sorter) => {
		const queryParams = new URLSearchParams(window.location.search);
		queryParams.set('page', pagination.current ? pagination.current : pagination);
		queryParams.set('limit', pagination?.pageSize ?? 10);
		if (sorter && Object.keys(sorter).length > 0) {
			queryParams.set('sort', sorter ? sorter.field : 'createdAt');
			queryParams.set('sortDirection', sorter ? sorter.order : filters.sortDirection);
		} else {
			queryParams.set('sort', 'createdAt');
			queryParams.set('sortDirection', 'descend');
		}
		const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
		window.history.pushState({}, '', newUrl);
		setFilters({
			...filters,
			sort: sorter ? sorter.field : 'createdAt',
			sortDirection: sorter ? sorter.order : 'descend',
			page: pagination.current ? pagination.current : pagination,
			limit: pagination.pageSize ? pagination.pageSize : 10,
		});
	};

	// Default empty form data
	const defaultFormData = {
		name: '',
		email: '',
		mobile: '',
		user_type: null,
		role: null,
		organization: null,
		is_active: true,
	};

	const handleAdd = () => {
		// Reset form and state before opening modal
		setFormData({ ...defaultFormData });
		form.resetFields();
		form.setFieldsValue({ ...defaultFormData });
		setIsModalLoading(false);
		setModalOpen([true, 'Add User', null]);
	};

	const handleEdit = (record) => {
		// Reset form before fetching edit data to prevent stale data showing
		setFormData({ ...defaultFormData });
		form.resetFields();
		form.setFieldsValue({ ...defaultFormData });
		setIsModalLoading(true);
		setModalOpen([true, 'Edit User', record.id]);
	};

	const handleCloseModal = () => {
		// Reset everything on close
		form.resetFields();
		setFormData({ ...defaultFormData });
		setIsModalLoading(false);
		setModalOpen([false, '', null]);
	};

	const handleDelete = (record) => {
		setSelectedUser(record);
		setDeleteModalVisible(true);
	};

	const confirmDelete = () => {
		if (selectedUser) {
			deleteUser({ id: selectedUser.id });
		}
	};

	const handleResendInvitation = (record) => {
		resendInvitation(record.id);
	};

	const handleSubmit = (values) => {
		// Determine if organization is required based on user_type
		const requiresOrganization =
			values.user_type === USER_TYPES.ORGANIZATION_ADMIN ||
			values.user_type === USER_TYPES.ORG_USER;

		// If user_type requires organization but none provided, show error
		if (requiresOrganization && !values.organization) {
			notification.error({
				message: 'Validation Error',
				description: 'Organization is required for this user type',
			});
			return;
		}

		// Build submit data
		const submitData = {
			...values,
			organization: requiresOrganization ? values.organization : null,
			is_active: values.is_active !== false, // Default to true
		};

		if (isEdit) {
			updateUser({ id: modalOpen[2], ...submitData });
		} else {
			createUser(submitData);
		}
	};

	// Get allowed user types based on current user type
	const getAllowedUserTypes = () => {
		if (userType === USER_TYPES.SUPER_ADMIN) {
			return USER_TYPE_OPTIONS;
		} else if (userType === USER_TYPES.ADMIN) {
			return USER_TYPE_OPTIONS.filter(
				(opt) => opt.value !== USER_TYPES.SUPER_ADMIN
			);
		} else if (userType === USER_TYPES.ORGANIZATION_ADMIN) {
			return USER_TYPE_OPTIONS.filter(
				(opt) => opt.value === USER_TYPES.ORG_USER
			);
		}
		return [];
	};

	// Get all roles (roles are now just permission sets)
	const getFilteredRoles = () => {
		return getRolesDropdownState.data || [];
	};

	// Filter organizations based on current user type
	const getFilteredOrganizations = () => {
		if (userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ADMIN) {
			return getOrganizationsDropdownState.data || [];
		} else if (userType === USER_TYPES.ORGANIZATION_ADMIN) {
			return (getOrganizationsDropdownState.data || []).filter(
				(org) => org._id === currentUserOrganizationId
			);
		}
		return [];
	};

	// Watch user_type field to determine if organization field should be shown
	const watchedUserType = ThemeForm.useWatch('user_type', form);
	const selectedUserType = watchedUserType || formData.user_type;
	const showOrganizationField =
		selectedUserType === USER_TYPES.ORGANIZATION_ADMIN ||
		selectedUserType === USER_TYPES.ORG_USER;

	const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: 'User Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (userType) => {
        const typeOption = USER_TYPE_OPTIONS.find((opt) => opt.value === userType);
        return <Tag color="purple">{typeOption?.label || userType}</Tag>;
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => {
        return <StatusTag status={isActive ? 'Active' : 'Inactive'} />;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {canEdit && (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleResendInvitation(record)}
              loading={resendInvitationState.apiState === 'loading'}
              title="Resend invitation email"
            >
              Resend
            </Button>
          )}
          {canDelete && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

	if (!permissions.authorized) {
		return (
			<ThemeMainWrapper>
				<MobHeader />
				<ForbiddenWrapper>
					<h2>403 - Forbidden</h2>
					<p>You do not have permission to access this page.</p>
				</ForbiddenWrapper>
			</ThemeMainWrapper>
		);
	}

  return (
    <>
      <MobHeader />
      <div className="customContainer">
        <Row gutter={[16, 16]} className="mb-3" align="middle">
          <Col xs={20} sm={12} md={12} lg={12}>
            <GreetingText>User Management</GreetingText>
          </Col>
          <Col xs={4} sm={12} md={12} lg={12} className="text-end">
            {canCreate && (
              <>
                {isMobile ? (
                  <ThemeButton
                    size="large"
                    shape="square"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  />
                ) : (
                  <ThemeButton
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  >
                    New User
                  </ThemeButton>
                )}
              </>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-1">
          <Col xs={24} sm={12} md={8} lg={8}>
            <ThemeInput
              placeholder="Search users..."
              size="large"
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={e => handleSearch(e.target.value)}
              onChange={e => {
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
          </Col>
          {/* <Col xs={24} sm={12} md={8} lg={8}>
						<ThemeFilterBtn size="large" shape="square">
							Filter
						</ThemeFilterBtn>
					</Col> */}
        </Row>
      </div>

      <div className="customBorders">
        <ThemeTable
          columns={columns}
          dataSource={tableData}
          loading={getUsersState.apiState === 'loading'}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: total => `Total ${total} users`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </div>

      {/* Add/Edit Modal */}
      <ThemeModal
        title={modalOpen[1]}
        centered
        open={modalOpen[0]}
        onCancel={handleCloseModal}
        footer={null}
        width={850}
      >
        {isModalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading...</div>
          </div>
        ) : (
          <ThemeForm
            form={form}
            name="userForm"
            layout="vertical"
            autoComplete="off"
            onFinish={handleSubmit}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter name' }]}
                >
                  <ThemeInput
                    size="large"
                    placeholder="Enter name"
                    name="name"
                    onChange={e => handleInputChange(e, formData, setFormData)}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter email' },
                    { type: 'email', message: 'Please enter valid email' },
                  ]}
                >
                  <ThemeInput
                    size="large"
                    placeholder="Enter email"
                    name="email"
                    disabled={isEdit && !isSuperAdmin}
                    onChange={e => handleInputChange(e, formData, setFormData)}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12}>
                <ThemeForm.Item label="Mobile" name="mobile">
                  <ThemeInput
                    size="large"
                    placeholder="Enter mobile"
                    name="mobile"
                    onChange={e => handleInputChange(e, formData, setFormData)}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="User Type"
                  name="user_type"
                  rules={[
                    { required: true, message: 'Please select user type' },
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="Select user type"
                    showSearch
                    filterOption={filterByLabel}
                    options={getAllowedUserTypes()}
                    onSelect={value => {
                      handleSelectChange(
                        'user_type',
                        value,
                        formData,
                        setFormData
                      );
                      // Clear organization if user_type doesn't require it
                      if (
                        value !== USER_TYPES.ORGANIZATION_ADMIN &&
                        value !== USER_TYPES.ORG_USER
                      ) {
                        form.setFieldsValue({ organization: null });
                        handleSelectChange(
                          'organization',
                          null,
                          formData,
                          setFormData
                        );
                      }
                    }}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12}>
                <ThemeForm.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true, message: 'Please select role' }]}
                >
                  <Select
                    size="large"
                    placeholder="Select role"
                    showSearch
                    filterOption={filterByLabel}
                    onFocus={() => getRolesDropdown()}
                    options={getFilteredRoles().map(role => ({
                      label: role.display_name,
                      value: role._id,
                    }))}
                    onSelect={value => {
                      handleSelectChange('role', value, formData, setFormData);
                    }}
                  />
                </ThemeForm.Item>
              </Col>

              {showOrganizationField && (
                <Col xs={24} sm={12}>
                  <ThemeForm.Item
                    label="Organization"
                    name="organization"
                    rules={[
                      {
                        required: true,
                        message: 'Please select organization',
                      },
                    ]}
                  >
                    <Select
                      size="large"
                      placeholder="Select organization"
                      showSearch
                      filterOption={filterByLabel}
                      onFocus={() => getOrganizationsDropdown()}
                      options={getFilteredOrganizations().map(org => ({
                        label: org.name,
                        value: org._id,
                      }))}
                      onSelect={value => {
                        handleSelectChange(
                          'organization',
                          value,
                          formData,
                          setFormData
                        );
                      }}
                    />
                  </ThemeForm.Item>
                </Col>
              )}

              {isEdit && (
                <Col xs={24} sm={12}>
                  <ThemeForm.Item
                    label="Active"
                    name="is_active"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                      onChange={checked => {
                        setFormData({ ...formData, is_active: checked });
                        form.setFieldsValue({ is_active: checked });
                      }}
                    />
                  </ThemeForm.Item>
                </Col>
              )}
            </Row>

            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} className="text-end">
                <Space>
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <ThemeButton
                    type="primary"
                    htmlType="submit"
                    loading={
                      createUserState.apiState === 'loading' ||
                      updateUserState.apiState === 'loading'
                    }
                  >
                    {isEdit ? 'Update User' : 'Create User'}
                  </ThemeButton>
                </Space>
              </Col>
            </Row>
          </ThemeForm>
        )}
      </ThemeModal>

      {/* Delete Modal */}
      <Modal
        title="Delete User"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedUser(null);
        }}
        confirmLoading={deleteUserState.apiState === 'loading'}
      >
        <p>{`Are you sure you want to delete user "${selectedUser?.name}"?`}</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
	getUsersState: state.user.getUsersState,
	deleteUserState: state.user.deleteUserState,
	createUserState: state.user.createUserState,
	updateUserState: state.user.updateUserState,
	getUserByIdState: state.user.getUserByIdState,
	resendInvitationState: state.user.resendInvitationState,
	getRolesDropdownState: state.role.getRolesDropdownState,
	getOrganizationsDropdownState: state.organization.getOrganizationsDropdownState,
});

const mapDispatchToProps = (dispatch) => ({
	getUsers: (params) => dispatch(getUsersApi(params)),
	getUsersReset: () => dispatch(getUsersReset()),
	deleteUser: (params) => dispatch(deleteUserApi(params.id)),
	deleteUserReset: () => dispatch(deleteUserReset()),
	createUser: (params) => dispatch(createUserApi(params)),
	createUserReset: () => dispatch(createUserReset()),
	updateUser: (params) => dispatch(updateUserApi(params.id, params)),
	updateUserReset: () => dispatch(updateUserReset()),
	getUserById: (params) => dispatch(getUserByIdApi(params.id)),
	getUserByIdReset: () => dispatch(getUserByIdReset()),
	resendInvitation: (id) => dispatch(resendInvitationApi(id)),
	resendInvitationReset: () => dispatch(resendInvitationReset()),
	getRolesDropdown: () => dispatch(getRolesDropdownApi()),
	getOrganizationsDropdown: () => dispatch(getOrganizationsDropdownApi()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserManagement);
